import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { C } from "@/brand/tokens";
import { publications, publicationBodies } from "@/brand/content";

/**
 * InsightsSection5 — Soft Parchment chamber.
 *
 * The formal thought-leadership surface. Not a blog — a publications
 * shelf: white papers, practice notes, field reports, and guides.
 *
 * Layout borrows from Concept 2's lower-page structure:
 *   - Featured publication on the left (larger, primary attention)
 *   - Three secondary publications in a compact hairline list on the
 *     right
 *   - "All publications" link at the bottom right
 */
export function InsightsSection5() {
  const [featured, ...rest] = publications;
  const featuredSlug = publicationBodies[0].slug;

  return (
    <section
      id="insights"
      data-testid="insights"
      style={{
        background: C.surfaceLight2,
        color: C.textOnLight,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-7">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                Publications &amp; Insights
              </p>
            </div>
            <h2
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(2.2rem, 4vw, 3.4rem)",
                fontWeight: 400,
                lineHeight: 1.06,
                letterSpacing: "-0.012em",
              }}
            >
              Practice notes, white papers,
              <br />
              and field reports.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:pt-6">
            <p
              className="font-inter leading-[1.7] max-w-[40ch]"
              style={{ color: C.inkOnLight, fontSize: "1rem" }}
            >
              Structured publications for organizational buyers making
              sense of people, culture, and conduct challenges.
            </p>
          </div>
        </div>

        <div
          className="grid lg:grid-cols-12 gap-10 border-t pt-14"
          style={{ borderColor: C.borderSoft }}
        >
          {/* Featured publication — col 1-7 */}
          <article
            data-testid="publication-featured"
            className="lg:col-span-7 group"
          >
            <div className="flex flex-wrap items-baseline gap-4 mb-7">
              <span
                className="font-inter text-[10px] uppercase font-bold"
                style={{
                  color: C.bgDark,
                  background: C.accentMetal,
                  letterSpacing: "0.28em",
                  padding: "6px 12px",
                }}
              >
                Featured
              </span>
              <span
                className="font-inter text-[10.5px] uppercase font-semibold tabular-nums"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                }}
              >
                {featured.tag} &nbsp;·&nbsp; {featured.date}
              </span>
            </div>
            <h3
              className="font-cormorant"
              style={{
                color: C.textOnLight,
                fontSize: "clamp(1.8rem, 3vw, 2.6rem)",
                fontWeight: 400,
                lineHeight: 1.08,
                letterSpacing: "-0.012em",
              }}
            >
              {featured.title}
            </h3>
            <p
              className="mt-6 font-inter leading-[1.7] max-w-[54ch]"
              style={{ color: C.inkOnLight, fontSize: "1.02rem" }}
            >
              {featured.kicker}
            </p>
            <div
              className="mt-8 pt-6 flex flex-wrap items-center justify-between gap-4"
              style={{ borderTop: `1px solid ${C.borderSoft}` }}
            >
              <span
                className="font-inter text-[11px] uppercase font-semibold"
                style={{
                  color: C.inkOnLightSoft,
                  letterSpacing: "0.24em",
                }}
              >
                {featured.length}
              </span>
              <a
                href={`/concept-e/insights/${featuredSlug}`}
                data-testid="publication-featured-link"
                className="group inline-flex items-center gap-2 pb-1.5 font-inter text-[11.5px] uppercase font-semibold border-b transition-colors"
                style={{
                  color: C.accentFunctional,
                  borderColor: C.accentFunctional,
                  letterSpacing: "0.22em",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = C.accentMetal;
                  e.currentTarget.style.borderColor = C.accentMetal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = C.accentFunctional;
                  e.currentTarget.style.borderColor = C.accentFunctional;
                }}
              >
                Read the paper
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </article>

          {/* Secondary publications list — col 9-12 */}
          <aside
            className="lg:col-span-5 lg:pl-8"
            data-testid="publications-list"
          >
            <p
              className="font-inter text-[10.5px] uppercase font-semibold pb-4"
              style={{
                color: C.accentMetal,
                letterSpacing: "0.28em",
                borderBottom: `1px solid ${C.borderSoft}`,
              }}
            >
              Further reading
            </p>
            <ul>
              {rest.map((p, i) => (
                <li
                  key={p.title}
                  data-testid={`publication-${i}`}
                  style={{ borderBottom: `1px solid ${C.borderSoft}` }}
                >
                  <Link
                    to={`/concept-e/insights/${publicationBodies[i + 1].slug}`}
                    className="group block py-6 transition-colors"
                  >
                    <div className="flex items-baseline gap-3 mb-3">
                      <span
                        className="font-inter text-[9.5px] uppercase font-bold tabular-nums"
                        style={{
                          color: C.accentMetal,
                          letterSpacing: "0.28em",
                        }}
                      >
                        {p.tag}
                      </span>
                      <span
                        aria-hidden="true"
                        className="flex-1 h-px"
                        style={{ background: C.borderSoft }}
                      />
                      <span
                        className="font-inter text-[9.5px] uppercase font-semibold tabular-nums"
                        style={{
                          color: C.inkOnLightSoft,
                          letterSpacing: "0.22em",
                        }}
                      >
                        {p.date}
                      </span>
                    </div>
                    <h4
                      className="font-cormorant"
                      style={{
                        color: C.textOnLight,
                        fontSize: "1.15rem",
                        fontWeight: 400,
                        lineHeight: 1.22,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {p.title}
                    </h4>
                    <p
                      className="mt-3 font-inter text-[12.5px] leading-[1.55]"
                      style={{ color: C.inkOnLightSoft }}
                    >
                      {p.length}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/concept-e/insights"
              data-testid="publications-all-link"
              className="group mt-8 inline-flex items-center gap-2 pb-1 font-inter text-[11px] uppercase font-semibold border-b transition-all"
              style={{
                color: C.accentMetal,
                borderColor: C.accentMetal,
                letterSpacing: "0.26em",
              }}
            >
              View all publications
              <ArrowRight
                size={12}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </aside>
        </div>
      </div>
    </section>
  );
}
